import React, { useState, useEffect, useRef } from "react";
import PdgDesktop from "./PdgDesktop";
import PdgWindow from "./PdgWindow";
import PdgFileExplorer from "./PdgFileExplorer";

export default function PdgOS({ code, myVariant, playerCount, socketRef, onAuthSuccess, apiBaseUrl }) {
  const [openApps, setOpenApps] = useState([]);
  const [copyProgress, setCopyProgress] = useState(null);
  const [copiedFilePaths, setCopiedFilePaths] = useState([]);
  const [usbEjected, setUsbEjected] = useState(false);
  const [usbModalDismissed, setUsbModalDismissed] = useState(false);
  const [infraLoaded, setInfraLoaded] = useState(false); // ✅ évite le flash
  const [explorerInitialPath, setExplorerInitialPath] = useState("/Desktop");
  const expectedProofCount = [4, 5, 6].includes(Number(playerCount)) ? Number(playerCount) : 6;

  // Au montage : vérifier l'état persisté en base via API REST
  useEffect(() => {
    if (!code) return;

    const base = apiBaseUrl || process.env.REACT_APP_API_URL || "http://localhost:4000";

    fetch(`${base}/api/play/session-infra?code=${encodeURIComponent(code)}`)
      .then((r) => r.json())
      .then((data) => {
        const copyState = data?.infraState?.stage3?.copy;
        if (copyState?.usbEjected === true || copyState?.status === "ejected") {
          setUsbEjected(true);
        }
      })
      .catch((e) => console.error("session-infra fetch error", e))
      .finally(() => setInfraLoaded(true));
  }, [code, apiBaseUrl]);

  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;

    const onCopySync = (payload) => {
      // Ignorer les events si déjà éjecté
      if (usbEjected) return;

      setCopyProgress(payload);

      if (payload?.files) {
        const done = payload.files.filter((f) => f.status === "copied").map((f) => f.path);
        setCopiedFilePaths(done);
      }

      if (payload?.status === "ejected") {
        setUsbEjected(true);
        setUsbModalDismissed(false);
        setCopyProgress(null);
        setCopiedFilePaths([]);
      }
    };

    const onEjected = () => {
      setUsbEjected(true);
      setUsbModalDismissed(false);
      setCopyProgress(null);
      setCopiedFilePaths([]);
    };

    socket.on("stage3:copy:sync", onCopySync);
    socket.on("stage3:copy:ejected", onEjected);

    return () => {
      socket.off("stage3:copy:sync", onCopySync);
      socket.off("stage3:copy:ejected", onEjected);
    };
  }, [socketRef, usbEjected]);

  // const openApp = (appId) => setOpenApps((prev) => prev.includes(appId) ? prev : [...prev, appId]);
  const openApp = (appId, options = {}) => {
    if (options.initialPath) setExplorerInitialPath(options.initialPath);
    setOpenApps((prev) => prev.includes(appId) ? prev : [...prev, appId]);
  };
  const closeApp = (appId) => setOpenApps((prev) => prev.filter((id) => id !== appId));

  const handleStartCopy = (filePath, fileName) => {
    socketRef?.current?.emit("stage3:copy:start", { filePath, fileName });
  };

  const handleEjectUsb = () => {
    socketRef?.current?.emit("stage3:copy:eject");
  };

  // Ne rien afficher tant que l'infra n'est pas chargée (évite le flash de la clé USB)
  if (!infraLoaded) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="text-xs text-slate-500 tracking-widest uppercase animate-pulse">
          Chargement…
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <PdgDesktop onOpenApp={openApp} usbEjected={usbEjected} />

      {openApps.includes("explorer") && (
        <PdgWindow title="Explorateur de fichiers" onClose={() => closeApp("explorer")} width="900px" height="560px">
          <PdgFileExplorer
            onStartCopy={handleStartCopy}
            copyProgress={copyProgress}
            copiedFilePaths={copiedFilePaths}
            myVariant={myVariant}
            onEjectUsb={handleEjectUsb}
            usbEjected={usbEjected}
            socketRef={socketRef}
            initialPath={explorerInitialPath}
            playerCount={playerCount}
          />
        </PdgWindow>
      )}

      {/* Modale éjection — fermable */}
      {usbEjected && !usbModalDismissed && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)" }}>
          <div className="w-[420px] rounded-xl overflow-hidden text-center"
            style={{
              background: "linear-gradient(135deg, #051a0f 0%, #0a1f0d 100%)",
              border: "1px solid rgba(52,211,153,0.5)",
              boxShadow: "0 0 80px rgba(52,211,153,0.25)",
            }}>
            <div style={{ height: "2px", background: "linear-gradient(90deg, transparent, #34d399, transparent)" }} />
            <div className="p-8">
              <div className="text-5xl mb-4" style={{ filter: "drop-shadow(0 0 24px rgba(52,211,153,1))" }}>
                💾
              </div>
              <div className="text-lg font-black tracking-[0.15em] uppercase mb-2"
                style={{ color: "#34d399", textShadow: "0 0 20px rgba(52,211,153,0.8)" }}>
                Clé USB récupérée
              </div>
              <div className="text-sm text-slate-400 leading-relaxed">
                Les preuves sont en sécurité.<br />
                <span className="text-emerald-300 font-semibold">Mission accomplie.</span>
              </div>
              <div className="mt-6 font-mono text-xs text-left rounded-lg p-4"
                style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(52,211,153,0.15)" }}>
                <div className="text-emerald-400">[USB]     Périphérique retiré en sécurité</div>
                <div className="text-emerald-300">[SYSTEM]  {expectedProofCount} fichiers de preuves sécurisés</div>
                <div className="text-slate-400">[SYSTEM]  Prêt pour l'opération suivante</div>
              </div>
              <button
                onClick={() => setUsbModalDismissed(true)}
                className="mt-6 w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition"
                style={{
                  background: "rgba(52,211,153,0.1)",
                  border: "1px solid rgba(52,211,153,0.3)",
                  color: "#6ee7b7",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(52,211,153,0.2)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(52,211,153,0.1)"}
              >
                Fermer
              </button>
            </div>
            <div style={{ height: "2px", background: "linear-gradient(90deg, transparent, #34d399, transparent)" }} />
          </div>
        </div>
      )}
    </div>
  );
}